import type { DataApiProject } from '@core/api/data-api.facade';
import {
  readProjectBlockchainIds,
  readProjectNetworkIds,
} from '@shared/utils/project-network-ids';

export { readProjectNetworkIds };

function nonEmptyTrimmed(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasExplicitEmptyIdArray(
  project: DataApiProject,
  keys: readonly string[],
): boolean {
  const row = project as unknown as Record<string, unknown>;
  for (const key of keys) {
    const value = row[key];
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
  }
  return false;
}

export function isProjectListingDraft(project: DataApiProject): boolean {
  const requiredTexts: Array<string | null | undefined> = [
    project.pitchDeck,
    project.whitepaper,
    project.legalOpinion,
    project.projectName,
    project.website,
    project.projectDescription,
    project.tokenInformation,
    project.teamInformation,
    project.stageOfDevelopment,
    project.twitter,
    project.github,
    project.totalRaised,
    project.investors,
    project.tokenSymbol,
  ];

  if (requiredTexts.some((t) => !nonEmptyTrimmed(t))) {
    return true;
  }

  if (readProjectBlockchainIds(project).length === 0) {
    if (
      hasExplicitEmptyIdArray(project, ['blockchainIds', 'BlockchainIds'])
    ) {
      return true;
    }

    const chains = project.projectBlockchains;
    if (
      Array.isArray(chains) &&
      chains.length > 0 &&
      !chains.some((pb) => pb.blockchainId != null)
    ) {
      return true;
    }
  }

  if (readProjectNetworkIds(project).length === 0) {
    if (hasExplicitEmptyIdArray(project, ['networkIds', 'NetworkIds'])) {
      return true;
    }
  }

  return false;
}

/** Human-readable gaps for project owner UI. */
export function projectListingDraftReasons(
  project: DataApiProject,
): string[] {
  const reasons: string[] = [];
  const labels: Array<[string, string | null | undefined]> = [
    ['Pitch deck', project.pitchDeck],
    ['Whitepaper', project.whitepaper],
    ['Legal opinion', project.legalOpinion],
    ['Project name', project.projectName],
    ['Website', project.website],
    ['Project description', project.projectDescription],
    ['Token information', project.tokenInformation],
    ['Team information', project.teamInformation],
    ['Stage of development', project.stageOfDevelopment],
    ['Twitter', project.twitter],
    ['GitHub', project.github],
    ['Total funds raised', project.totalRaised],
    ['Investors', project.investors],
    ['Token symbol', project.tokenSymbol],
  ];

  for (const [label, value] of labels) {
    if (!nonEmptyTrimmed(value)) {
      reasons.push(label);
    }
  }

  if (
    readProjectBlockchainIds(project).length === 0 &&
    (hasExplicitEmptyIdArray(project, ['blockchainIds', 'BlockchainIds']) ||
      (Array.isArray(project.projectBlockchains) &&
        project.projectBlockchains.length > 0))
  ) {
    reasons.push('At least one blockchain');
  }

  if (
    readProjectNetworkIds(project).length === 0 &&
    hasExplicitEmptyIdArray(project, ['networkIds', 'NetworkIds'])
  ) {
    reasons.push('At least one network');
  }

  return reasons;
}
